import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { TableCell, TableCellProps, Theme, useTheme } from "@mui/material";
import { format } from "date-fns";
import { makeStyles } from "helpers/StyleHelper";
import { get } from "lodash";
import React, { forwardRef, useEffect, useRef, useState } from "react";

import type { BooleanCellType, NodeCellType, TableCellType } from "./index";

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    width: "100%",
  },
  paper: {
    width: "100%",
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down("xs")]: {
      marginLeft: theme.spacing(-2),
      marginRight: theme.spacing(-2),
      width: "auto",
      boxShadow: "none",
    },
  },
  dateCell: {
    whiteSpace: "nowrap",
  },
}));

const priceFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
});

const BasicCell = ({
  cellProps,
  value,
}: {
  cellProps: TableCellProps;
  value: string;
}) => <TableCell {...cellProps}>{value}</TableCell>;

const BooleanCell = ({
  cellProps,
  value,
  TrueComponent,
  FalseComponent,
}: {
  cellProps: TableCellProps;
  value: boolean;
  TrueComponent?: React.ReactNode;
  FalseComponent?: React.ReactNode;
}) => (
  <TableCell {...cellProps}>
    {value ? TrueComponent ?? <CheckIcon /> : FalseComponent ?? <CloseIcon />}
  </TableCell>
);

const DateCell = ({
  cellProps,
  value,
  className,
}: {
  cellProps: TableCellProps;
  value: Date;
  className?: string;
}) => (
  <TableCell {...cellProps} className={className}>
    {value ? format(value, "P") : null}
  </TableCell>
);

const DateTimeCell = ({
  cellProps,
  value,
  className,
}: {
  cellProps: TableCellProps;
  value: Date;
  className?: string;
}) => (
  <TableCell {...cellProps} className={className}>
    {value ? format(value, "Pp") : null}
  </TableCell>
);

const PriceCell = ({
  cellProps,
  value,
  className,
}: {
  cellProps: TableCellProps;
  value: number;
  className?: string;
}) => (
  <TableCell {...cellProps} className={className}>
    {value ? priceFormatter.format(value) : null}
  </TableCell>
);

type EnhancedTableCellProps = {
  cell: TableCellType;
  row: object;
  dragStarting?: boolean;
};

const TypedCell = forwardRef<HTMLElement, EnhancedTableCellProps>(
  (props, ref) => {
    const styles = useStyles(useTheme());
    const { cell, row } = props;

    const cellProps = {
      className: cell.className,
      align: cell.align,
      ref: ref,
    } as TableCellProps;
    const value = get(row, cell.accessor);
    switch (cell.type) {
      case "boolean":
        return (
          <BooleanCell
            cellProps={cellProps}
            TrueComponent={(cell as BooleanCellType).TrueComponent}
            FalseComponent={(cell as BooleanCellType).FalseComponent}
            value={value}
          />
        );
      case "date":
        return (
          <DateCell
            cellProps={{ ...cellProps, sx: styles.dateCell }}
            className={cell.className}
            value={value}
          />
        );
      case "datetime":
        return (
          <DateTimeCell
            cellProps={{ ...cellProps, sx: styles.dateCell }}
            className={cell.className}
            value={value}
          />
        );
      case "node": {
        const View = (cell as NodeCellType).View;
        return (
          <TableCell {...cellProps}>
            <View value={row} />
          </TableCell>
        );
      }
      case "price":
        return <PriceCell cellProps={cellProps} value={value} />;
      default:
        return <BasicCell cellProps={cellProps} value={value} />;
    }
  }
);

function useTableCellSnapshot(dragStarting: boolean = false) {
  const [snapshot, setSnapshot] = useState<{
    width: number;
    height: number;
  } | null>(null);
  const ref = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!ref.current) {
      setSnapshot(null);
      return;
    }

    // capturing table row width before drag starts to avoid width shrinking
    if (!dragStarting) {
      const { width, height } = ref.current.getBoundingClientRect();
      setSnapshot({ width, height });
    }
  }, [dragStarting]);

  return { childRef: ref, snapshot };
}

const EnhancedTableCell = ({
  cell,
  row,
  dragStarting,
}: EnhancedTableCellProps) => {
  const { childRef, snapshot } = useTableCellSnapshot(dragStarting);

  useEffect(() => {
    const currentRef = childRef.current;

    if (currentRef && snapshot) {
      currentRef.style.width = `${snapshot.width}px`;
      currentRef.style.height = `${snapshot.height}px`;
    }
  }, [dragStarting, snapshot, childRef]);
  return <TypedCell cell={cell} row={row} ref={childRef} />;
};

export default EnhancedTableCell;
